import React, { useEffect, useState } from "react";
import { FiltersButton, InfoBanner, Spinner, TableHeader } from "@services/ui-components";
import { Col, Row } from "react-bootstrap";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { useReactiveVar } from "@apollo/client";
import ReportsFilterWrapper from "./ReportsFilterWrapper";
import ReportsTable from "./ReportsTable";
import { ReportsFilter } from "./types";
import { currentBranchIdVar } from "@me-team/host/main/globalVar/state";
import {
    getCurrentStepFromLocalStorage,
    getWarehouseTutorialType,
    isWarehouseTutorialActive,
} from "../../Joyride/WarehouseTutorial/joyrideStepHelpers";
import WarehouseJoyrideWrapper from "../../Joyride/WarehouseTutorial/WarehouseJoyrideWrapper";
import { useWarehouseReportsAccess } from "./useWarehouseReportsAccess";

const Reports: React.FC = () => {
    const { t } = useTranslation();
    const currentBranchId = useReactiveVar(currentBranchIdVar);
    const [showFilters, setShowFilters] = useState(true);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const tutorialActive = isWarehouseTutorialActive();
    const tutorialType = getWarehouseTutorialType();
    const startStep = getCurrentStepFromLocalStorage();
    const {
        canViewWarehouseReports,
        shouldShowOnlyOwnReports,
        isWarehouseReportsAccessPending,
        lockedEmployeeId,
        currentBranchEmployeeId,
    } = useWarehouseReportsAccess();

    const initialValueFilter: ReportsFilter = {
        startDate: null,
        endDate: null,
        reportType: null,
        categoryId: null,
        skuBarcode: null,
        warehouseId: null,
        employeeId: lockedEmployeeId,
    };

    const [filters, setFilters] = useState<ReportsFilter>(initialValueFilter);

    const toggleFilters = () => setShowFilters(!showFilters);

    useEffect(() => {
        if (!shouldShowOnlyOwnReports || !lockedEmployeeId) return;

        setFilters((prevFilters) =>
            prevFilters.employeeId === lockedEmployeeId ? prevFilters : { ...prevFilters, employeeId: lockedEmployeeId }
        );
    }, [lockedEmployeeId, shouldShowOnlyOwnReports]);

    const isCurrentBranchEmployeePending = !currentBranchEmployeeId;
    const isLockedEmployeePending = shouldShowOnlyOwnReports && !lockedEmployeeId;

    if (!currentBranchId || isWarehouseReportsAccessPending || isCurrentBranchEmployeePending || isLockedEmployeePending) return <Spinner />;

    if (!canViewWarehouseReports) return null;

    return (
        <div className="mb-9 px-2 px-lg-0" data-tour="reports">
            <InfoBanner>
                <p className="fs-7 mb-0">
                    {t(
                        "To view the report, select the time period, report type, and click Generate. To view detailed information, click “∨” in the line with the product."
                    )}
                </p>
            </InfoBanner>
            <TableHeader
                title={t("Reports")}
                paths={[
                    {
                        path: `/warehouse/reports/branch/${currentBranchId}`,
                        label: t("Warehouse accounting"),
                    },
                    {
                        path: `/warehouse/reports/branch/${currentBranchId}`,
                        label: t("Reports"),
                    },
                ]}
                colSize={9}
                btnColSize={3}
                buttons={
                    <Row className="gx-3 justify-content-end align-items-center mt-4 mt-lg-0">
                        <Col xs={12} md={6} lg={7} xxl={5}>
                            <FiltersButton
                                filter={filters}
                                showFilters={showFilters}
                                onClick={toggleFilters}
                                classes="mb-3 my-lg-0"
                            />
                        </Col>
                    </Row>
                }
                showFilters={showFilters}
            >
                <ReportsFilterWrapper
                    setFilters={setFilters}
                    shouldShowOnlyOwnReports={shouldShowOnlyOwnReports}
                    lockedEmployeeId={lockedEmployeeId}
                    isWarehouseReportsAccessPending={isWarehouseReportsAccessPending}
                />
            </TableHeader>
            <ReportsTable
                filters={filters}
                canViewWarehouseReports={canViewWarehouseReports}
                shouldShowOnlyOwnReports={shouldShowOnlyOwnReports}
                lockedEmployeeId={lockedEmployeeId}
                currentBranchEmployeeId={currentBranchEmployeeId}
                isWarehouseReportsAccessPending={isWarehouseReportsAccessPending}
            />

            {tutorialActive && tutorialType && (
                <WarehouseJoyrideWrapper tutorialType={tutorialType} startStep={startStep} />
            )}

            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </div>
    );
};

export default Reports;
